import { flatMap, cloneDeep, forEach, set, map, values, compact, isNil, get } from 'lodash';
import { Identifiable, Modifiable, Schedulable } from '../../utils';
import { AssignmentMeta, AssignmentType } from '../assignment';
import { QuizQuestion, UserReference } from '../common';
import { AssignmentEntryUpdateData } from './AssignmentEntryUpdateData';
import { AnyAnswer, AnyAnswerResult, AnyPinEntry, PinEntry } from './PinEntry';

export type AssignmentEntryStatus = 'kicked' | 'default' | 'left';
export type AssignmentEntryStatusWithDisplayName = Exclude<AssignmentEntryStatus, 'kicked'>;
/**
 * This object contains all the answers for the given assignment (for a single user)
 */
export interface AssignmentEntry extends Identifiable, Modifiable, Schedulable {
  userId: string;
  createdAt: number;
  assignmentId: string;
  type: AssignmentType;
  displayName?: string | null;
  familyName?: string;
  entries: { [pinId: string]: AnyPinEntry | null | undefined };
  totalPins: number;
  status: AssignmentEntryStatus;
  seenAt?: number;
  activePinId?: string; // used in linear mode to determine the validity of the current pin.
  logins?: { ts: number }[];
}

export type AssignmentEntryWithDisplayName = AssignmentEntry & {
  displayName: string;
  status: AssignmentEntryStatusWithDisplayName;
};

export namespace AssignmentEntry {
  export const excludedComparisonPaths: string[] = ['createdAt', 'modifiedAt'];
  export const maxRegisteredLoginCount = 50;

  export interface ShuffleMap {
    questions: {
      [pinId: string]: QuizQuestion.AnswerValue[];
    };
    pinOrder: string[];
  }

  export function createIds(assignmentIds: string[], userIds: string[]): string[] {
    return flatMap(assignmentIds, (assignmentId) => userIds.map((userId) => createId(assignmentId, userId)));
  }

  export function createId(assignmentId: string, userId: string): string {
    return `${assignmentId}-${userId}`;
  }

  export function splitIdIntoComponents(entryId: string): { assignmentId: string; userId: string } {
    const [assignmentId, userId] = entryId.split('-');
    return { assignmentId, userId };
  }

  export function create(meta: AssignmentMeta, user: UserReference): AssignmentEntry {
    const now = Date.now();

    return {
      _id: createId(meta._id, user._id),
      createdAt: now,
      type: meta.type,
      modifiedAt: now,
      assignmentId: meta._id,
      userId: user._id,
      displayName: user.displayName ?? undefined,
      familyName: user.familyName ?? undefined,
      totalPins: meta.assignmentInfo.numberOfPins,
      entries: {},
      status: 'default',
    };
  }

  /** creates a clone of the given entry and applies the given update logic so we can use it for e.g. comparisons */
  export function update(assignmentEntry: AssignmentEntry, updates: AssignmentEntryUpdateData): AssignmentEntry {
    const cloned = cloneDeep(assignmentEntry);
    const updateFields = AssignmentEntryUpdateData.updateFields(updates);
    forEach(updateFields, (value, field) => set(cloned, field, value));
    return cloned;
  }

  export function createForUsers(meta: AssignmentMeta, users: UserReference[]): AssignmentEntry[] {
    return map(users, (user) => AssignmentEntry.create(meta, user));
  }

  export function doneCount(entry: AssignmentEntry, onlyInteractive = false): number {
    return values(entry.entries)
      .filter((pinEntry) => (onlyInteractive ? PinEntry.isInteractive(pinEntry) : true))
      .filter((pinEntry) => pinEntry && pinEntry.done === true).length;
  }

  export function pinEntries(assignmentEntry: AssignmentEntry): AnyPinEntry[] {
    return compact(values(assignmentEntry.entries));
  }

  export function pinEntryFromEntries(entries: AssignmentEntry[], pinId: string | undefined): AnyPinEntry[] {
    if (isNil(pinId)) {
      return [];
    }
    return compact(map(entries, (value) => AssignmentEntry.entryForPin(value, pinId)));
  }

  export function pinEntriesWithDisplayNameFromEntries(
    entries: AssignmentEntry[],
    pinId: string | undefined
  ): PinEntry.WithDisplayName[] {
    if (isNil(pinId)) {
      return [];
    }

    return compact(
      entries.map((value) => {
        const entry = entryForPin(value, pinId);
        if (!entry) return undefined;

        if (!hasDisplayName(value)) return undefined;

        return {
          ...entry,
          displayName: value.displayName,
        };
      })
    );
  }

  export function entryForPin<T extends AnyPinEntry = AnyPinEntry>(
    entry: AssignmentEntry | undefined,
    pinId: string | undefined
  ): T | undefined {
    return pinId ? (get(entry && entry.entries, pinId) as T) : undefined;
  }

  export function answerForPinEntry<T extends AnyAnswer = AnyAnswer>(pinEntry: AnyPinEntry | undefined) {
    if (!pinEntry) {
      return undefined;
    }
    return pinEntry.answer as T;
  }

  export function answerForPin<T extends AnyAnswer = AnyAnswer>(
    entry: AssignmentEntry | undefined,
    pinId: string | undefined
  ): T | undefined {
    const pinEntry = entryForPin(entry, pinId);
    if (!pinEntry) {
      return undefined;
    }
    return pinEntry.answer as T | undefined;
  }

  export function resultForPin<T extends AnyAnswerResult = AnyAnswerResult>(
    entry: AssignmentEntry | undefined,
    pinId: string | undefined
  ): T | undefined {
    const pinEntry = entryForPin(entry, pinId);
    if (!pinEntry) {
      return undefined;
    }
    return pinEntry.result as T;
  }

  export function hasStatus(entry: AssignmentEntry | undefined, status: AssignmentEntryStatus): boolean {
    return entry?.status === status;
  }

  /** returns entries that match one of the given statusses */
  export function filterByStatus(
    entries: AssignmentEntry[],
    status: AssignmentEntryStatus | AssignmentEntryStatus[]
  ): AssignmentEntry[] {
    const statuses = Array.isArray(status) ? status : [status];
    return entries.filter((entry) => statuses.includes(entry.status));
  }

  export function calcPercentagePinsDone(entry: AssignmentEntry): number {
    const total = entry.totalPins;
    const count = Object.values(entry.entries).filter((e) => e && e.done).length;
    if (total == 0) return 0;
    return (count / total) * 100;
  }

  export function isKicked(entry: AssignmentEntry): boolean {
    return hasStatus(entry, 'kicked');
  }

  export function hasDisplayName(entry: AssignmentEntry): entry is AssignmentEntryWithDisplayName {
    return Boolean(entry.displayName && entry.displayName.length);
  }

  export function isActiveEntry(entry: AssignmentEntry): boolean {
    return hasStatus(entry, 'default');
  }

  export function shouldHaveBookmark(entry: AssignmentEntry): boolean {
    // Any entry should have a bookmark, except when the match the following criteria.
    return !isKicked(entry);
  }

  export function loginCountDuringTest(entry: AssignmentEntry): number {
    return entry?.logins?.length || 0;
  }
}
